import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Add, CloseRounded, MoreVert } from "@mui/icons-material";
import { v4 as uuid } from "uuid";
import { debounce } from "lodash";
import { DateTime } from "luxon";
import { AxiosResponse } from "axios";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import {
  dataTableListShell,
  dataTablePageShell,
} from "../../utils/dataTableShell";
import { HeaderStyle } from "../Common/styles/header";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";
import CanonicalQuestionModal, {
  CANONICAL_QUESTION_TYPES,
} from "./CanonicalQuestionModal";

const CanonicalQuestions = () => {
  const [searchText, setSearchText] = useState("");
  const [searchId, setSearchId] = useState<string>(uuid());
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [menuLoader, setMenuLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "view">("create");
  const [viewLoader, setViewLoader] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(page + 1));
        params.set("size", String(pageSize));
        if (searchText) params.set("search", searchText);
        if (typeFilter) params.set("type", typeFilter);

        const res: AxiosResponse = await http.get(
          `/admin/registry/canonical-questions?${params.toString()}`
        );
        const rows = res?.data?.data?.data ?? [];
        const newData = rows.map((item: any) => ({
          id: item?.id,
          label: item?.label,
          varname: item?.varname,
          type: item?.type,
          format: item?.format ?? "",
          choices: item?.choices ?? [],
          choicesCount: item?.choices?.length ?? 0,
          linksCount: item?.links?.length ?? 0,
          date: item?.createdAt
            ? DateTime.fromISO(item.createdAt).toFormat("MM/dd/yyyy")
            : "",
        }));
        setData(newData);
        setDataCount(
          res?.data?.data?.meta?.paginationOptions?.totalItems ?? 0
        );
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [toggleLoader, searchText, typeFilter, page]);

  const modifySearchTerm = useMemo(
    () =>
      debounce((val: string) => {
        setPage(0);
        setSearchText(val);
      }, 500),
    []
  );

  const handleClearSearch = () => {
    setSearchText("");
    setPage(0);
    setSearchId(uuid());
  };

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openCreateModal = () => {
    setSelectedRow(null);
    setModalMode("create");
    setShowModal(true);
  };

  const openViewModal = async (row: any) => {
    if (!row?.id || viewLoader) return;
    try {
      setViewLoader(true);
      handleMenuClose();
      const res: AxiosResponse = await http.get(
        `/admin/registry/canonical-questions/${row.id}`
      );
      const item = res?.data?.data;
      setSelectedRow({
        id: item?.id,
        label: item?.label,
        varname: item?.varname,
        type: item?.type,
        format: item?.format ?? "",
        choices: item?.choices ?? [],
      });
      setModalMode("view");
      setShowModal(true);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setViewLoader(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
    setModalMode("create");
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      setMenuLoader(true);
      const res: AxiosResponse = await http.delete(
        `/admin/registry/canonical-questions/${selectedRow.id}`
      );
      toastMessage("success", res.data.message);
      refreshPage();
      handleMenuClose();
      setSelectedRow(null);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setMenuLoader(false);
    }
  };

  return (
    <>
      <Box sx={HeaderStyle}>
        <Typography variant="h2" color="text.primary">
          Canonical Questions
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreateModal}
          >
            Add Question
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 2.5, ...dataTablePageShell("calc(100% - 72px)") }}>
        <Box
          sx={{
            ...dataTableListShell,
            p: 2,
            backgroundColor: "#fff",
            borderRadius: "10px",
          }}
        >
          <DataTableLayout
            toolbar={
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  mb: 2,
                }}
              >
                <TextField
                  key={searchId}
                  placeholder="Search by label"
                  style={{ width: "30%" }}
                  defaultValue={searchText}
                  onChange={(e) => modifySearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchText && (
                      <InputAdornment position="end">
                        <IconButton onClick={handleClearSearch}>
                          <CloseRounded />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    displayEmpty
                    value={typeFilter}
                    onChange={(e) => {
                      setPage(0);
                      setTypeFilter(e.target.value as string);
                    }}
                  >
                    <MenuItem value="">All types</MenuItem>
                    {CANONICAL_QUESTION_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            }
            footer={
              <DataTableFooter
                filteredCount={dataCount}
                page={page}
                pageSize={pageSize}
                onPageChange={handleChangePage}
                loading={loading}
              />
            }
          >
            <Table
              sx={TableBorderRadiusTopLeftRight}
              stickyHeader
              className="scrollable-table"
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Label</StyledTableCell>
                  <StyledTableCell>Varname</StyledTableCell>
                  <StyledTableCell>Type</StyledTableCell>
                  <StyledTableCell>Choices count</StyledTableCell>
                  <StyledTableCell>Created</StyledTableCell>
                  <StyledTableCell />
                </TableRow>
              </TableHead>
              {!loading && data?.length > 0 && (
                <TableBody>
                  {data.map((row: any) => (
                    <TableRow key={row.id}>
                      <StyledTableCell>
                        <Typography
                          component="span"
                          onClick={() => openViewModal(row)}
                          sx={{
                            color: "primary.main",
                            cursor: viewLoader ? "wait" : "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {row.label || "-"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>{row.varname || "-"}</StyledTableCell>
                      <StyledTableCell
                        sx={{ textTransform: "capitalize" }}
                      >
                        {row.type || "-"}
                      </StyledTableCell>
                      <StyledTableCell>{row.choicesCount}</StyledTableCell>
                      <StyledTableCell>{row.date || "-"}</StyledTableCell>
                      <StyledTableCell align="right">
                        <IconButton onClick={(e) => handleMenuClick(e, row)}>
                          <MoreVert />
                        </IconButton>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!loading && data.length === 0 && (
              <NoDataContainer>
                <Typography variant="body1" color="gray">
                  No Data
                </Typography>
              </NoDataContainer>
            )}
            {loading && (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
          </DataTableLayout>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            PaperProps={{
              style: {
                maxHeight: "400px",
                width: "20ch",
              },
            }}
            TransitionProps={{
              exit: false,
            }}
          >
            <MenuItem
              onClick={() => openViewModal(selectedRow)}
              disabled={viewLoader}
            >
              <ListItemText>View</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete} disabled={menuLoader}>
              <ListItemText>Delete</ListItemText>
              {menuLoader && (
                <ListItemIcon>
                  <CircularProgress size={18} sx={{ ml: 1 }} />
                </ListItemIcon>
              )}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {showModal && (
        <CanonicalQuestionModal
          data={selectedRow}
          open={showModal}
          closeModal={closeModal}
          refreshPage={refreshPage}
          readOnly={modalMode === "view"}
        />
      )}
    </>
  );
};

export default CanonicalQuestions;
